import React from "react";
import { useTranslation } from "react-i18next";
import StopIcon from "@mui/icons-material/Stop";

export function getKotStartRoundButtonLabel(
  t,
  { starting = false, preparingNextRound = false, roundNumber = null } = {}
) {
  if (starting) return t("kotStartingRound") || "Starting…";
  if (preparingNextRound) return t("kotPreparingNextRound") || "Preparing…";
  if (roundNumber != null) {
    return t("kotStartRoundNumbered", {
      n: roundNumber,
      defaultValue: `Start Round #${roundNumber}`,
    });
  }
  return t("kotStartRound") || "Start Round";
}

/**
 * Compact Start-round control for session controller / planning card headers.
 */
/**
 * Stop-on-score-lead: coach stops the round so all tables can enter results.
 */
export function KotCoachStopRoundButton({
  visible = false,
  stopping = false,
  onStop,
  className = "",
}) {
  const { t } = useTranslation();
  if (!visible) return null;

  return (
    <button
      type="button"
      className={[
        "kot-coach-stop__button",
        "kot-coach-stop__button--actions-bar",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={(e) => {
        e.stopPropagation();
        if (onStop && !stopping) onStop();
      }}
      disabled={stopping}
      aria-label={t("kotCoachStopRound")}
    >
      <StopIcon fontSize="small" />
      {stopping
        ? t("kotCoachStoppingRound")
        : t("kotCoachStopRound")}
    </button>
  );
}

export function KotCoachStartRoundButton({
  canStartRound = false,
  awaitingCoachStart = false,
  preparingNextRound = false,
  roundNumber = null,
  starting = false,
  onStart,
  className = "",
}) {
  const { t } = useTranslation();

  if (!awaitingCoachStart) return null;

  const label = getKotStartRoundButtonLabel(t, {
    starting,
    preparingNextRound,
    roundNumber,
  });

  return (
    <button
      type="button"
      className={[
        "kot-coach-start__button",
        "kot-coach-start__button--header",
        canStartRound && !starting ? "kot-coach-start__button--ready" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={(e) => {
        e.stopPropagation();
        if (onStart && canStartRound) onStart();
      }}
      disabled={starting || !canStartRound}
      aria-label={
        canStartRound && roundNumber != null
          ? getKotStartRoundButtonLabel(t, { roundNumber })
          : t("kotStartRoundWhenReady") ||
            "Start new round will be available when every table has submitted."
      }
      title={
        !canStartRound
          ? preparingNextRound
            ? t("kotPreparingNextRound") ||
              "All tables submitted — preparing next round…"
            : t("kotStartRoundWhenReady") ||
              "Start new round will be available when the exercise is running."
          : undefined
      }
    >
      {label}
    </button>
  );
}

/**
 * Coach CTA: submission progress during a round, Start button when next round is PENDING.
 */
export function KotCoachStartRoundBanner({
  canStartRound = false,
  awaitingCoachStart = false,
  preparingNextRound = false,
  roundClosing = false,
  tableDataSyncPending = false,
  showSubmissionProgress = false,
  submissionSummary = { total: 0, submitted: 0, pending: 0, allSubmitted: false },
  roundNumber = null,
  starting = false,
  startError = null,
  onStart,
  onDismissError,
  compact = false,
  rotationMoves = null,
  /** When false, progress/messages stay in-panel; button lives in session controller header. */
  showStartButton = true,
}) {
  const { t } = useTranslation();
  const { total, submitted, pending } = submissionSummary;

  if (
    !showSubmissionProgress &&
    !awaitingCoachStart &&
    !roundClosing &&
    !tableDataSyncPending &&
    !startError
  ) {
    return null;
  }

  const progressVisible =
    (showSubmissionProgress || roundClosing || tableDataSyncPending) &&
    total > 0;
  const allTablesDone =
    preparingNextRound ||
    roundClosing ||
    (total > 0 && submissionSummary.pending === 0);

  return (
    <div
      className={compact ? "kot-coach-start kot-coach-start--compact" : "kot-coach-start"}
      onClick={(e) => e.stopPropagation()}
    >
      {progressVisible ? (
        <div className="kot-coach-start__progress">
          <span className="kot-coach-start__progress-label">
            {t("kotTablesSubmittedProgress", {
              submitted,
              total,
              defaultValue: `${submitted}/${total} tables submitted`,
            })}
          </span>
          {allTablesDone ? (
            <span className="kot-coach-start__progress-hint kot-coach-start__progress-hint--done">
              {roundClosing
                ? t("kotClosingRound") ||
                  "All tables submitted — closing round…"
                : t("kotPreparingNextRound") ||
                  "All tables submitted — preparing next round…"}
            </span>
          ) : pending > 0 ? (
            <span className="kot-coach-start__progress-hint">
              {t("kotStartRoundWhenReady") ||
                "Start round will be available when every table has submitted."}
            </span>
          ) : null}
          {tableDataSyncPending ? (
            <span className="kot-coach-start__progress-hint">
              {t("kotTableDataSyncPending") ||
                "Waiting for table data sync…"}
            </span>
          ) : null}
        </div>
      ) : null}

      {awaitingCoachStart || (roundClosing && Array.isArray(rotationMoves) && rotationMoves.length > 0) ? (
        <div className="kot-coach-start__ready">
          <p className="kot-coach-start__message">
            {roundClosing && !awaitingCoachStart
              ? t("kotPreparingNextRound") ||
                "All tables submitted — preparing next round…"
              : t("kotTransitionBanner")}
          </p>
          {Array.isArray(rotationMoves) && rotationMoves.length > 0 ? (
            <ul className="kot-coach-start__rotation">
              {rotationMoves.slice(0, 8).map((move, idx) => {
                const name =
                  move.user_alias ||
                  move.player_name ||
                  move.player_id ||
                  "—";
                const from = move.from_table_number ?? move.from_table ?? "?";
                const to = move.to_table_number ?? move.to_table ?? "?";
                return (
                  <li key={move.player_id || idx}>
                    {name}: {t("table_with_number", { number: from })} →{" "}
                    {t("table_with_number", { number: to })}
                  </li>
                );
              })}
            </ul>
          ) : null}
          {showStartButton && awaitingCoachStart ? (
            <div className="kot-coach-start__cta">
              <KotCoachStartRoundButton
                canStartRound={canStartRound}
                awaitingCoachStart={awaitingCoachStart}
                preparingNextRound={preparingNextRound}
                roundNumber={roundNumber}
                starting={starting}
                onStart={onStart}
              />
            </div>
          ) : null}
          {!canStartRound ? (
            <p className="kot-coach-start__progress-hint" style={{ marginTop: 8 }}>
              {preparingNextRound
                ? t("kotPreparingNextRound") ||
                  "All tables submitted — preparing next round…"
                : t("kotStartRoundWhenReady") ||
                  "Start round will be available when the exercise is running."}
            </p>
          ) : null}
        </div>
      ) : null}

      {startError ? (
        <div className="kot-coach-start__error">
          <span>{startError}</span>
          {onDismissError ? (
            <button
              type="button"
              className="kot-coach-start__error-dismiss"
              onClick={(e) => {
                e.stopPropagation();
                onDismissError();
              }}
            >
              ×
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default KotCoachStartRoundBanner;
